import { useCallback, useMemo } from 'react'
import { useRaidPlanCastHeatmap } from 'app/frontend/base/config/queries'
import { useTeamPlanTimeline } from '../components/TeamPlanTimelineContext'
import {
  HeatmapSegment,
  projectCachedHeatmap,
  specHeatmapKey,
  classHeatmapKey,
} from '../utils/castHeatmap'

export type RowHeatmapLookup = (input: {
  classId: number
  specId: number | null
  spellId: number
}) => HeatmapSegment[] | undefined

const useCastHeatmap = (enabled: boolean) => {
  const { owner, raidPlan, computed } = useTeamPlanTimeline()
  const { phases, phaseStarts, effectivePhaseDurations } = computed
  const query = useRaidPlanCastHeatmap(
    owner,
    raidPlan.encounterId,
    raidPlan.difficulty,
    enabled,
  )
  const heatmap = query.data
  const projectionCache = useMemo(
    () => new Map<string, HeatmapSegment[]>(),
    [effectivePhaseDurations, heatmap, phaseStarts, phases],
  )

  const getRowHeatmap = useCallback<RowHeatmapLookup>(
    ({ classId, specId, spellId }) => {
      if (!heatmap) return undefined
      const key =
        specId != null
          ? specHeatmapKey(specId, spellId)
          : classHeatmapKey(classId, spellId)
      if (projectionCache.has(key)) {
        const cached = projectionCache.get(key) ?? []
        return cached.length > 0 ? cached : undefined
      }
      const percentages = heatmap.groups[key]?.phases
      if (!percentages) {
        projectionCache.set(key, [])
        return undefined
      }
      const segments = projectCachedHeatmap({
        percentagesByPhaseId: percentages,
        phases,
        phaseStarts,
        effectivePhaseDurations,
      })
      projectionCache.set(key, segments)
      return segments.length > 0 ? segments : undefined
    },
    [effectivePhaseDurations, heatmap, phaseStarts, phases, projectionCache],
  )

  return {
    isLoading: enabled && query.isLoading,
    isFetched: query.isFetched,
    killCount: heatmap?.killCount ?? 0,
    getRowHeatmap,
  }
}

export default useCastHeatmap
